import { Button } from '@/components/ui/Button';
import { PUBLIC_BODY_CLASS } from '../public-text';
import { DEFAULT_MISSION_SPLIT_IMAGE, DEFAULT_INVESTOR_BENEFITS_IMAGES, imageOrDefault } from '../default-images';
import { CtaLink } from '../CtaLink';
import type { SectionRendererProps } from '../section-renderer.types';

interface MissionSplitContent {
  eyebrow?: string;
  title: string;
  body?: string[];
  image?: string;
  ctaLabel?: string;
  ctaUrl?: string;
}

// Investors' benefits-split section (MYN-255/MYN-214, confirmed via Figma's
// own get_design_context data) is a cream-to-mint gradient surface with dark
// text and a 3-photo collage, not Home's dark navy full-bleed band - keyed
// the same way CtaBannerRenderer keys its own per-section color differences.
const LIGHT_VARIANT = new Set(['investor-benefits']);

/**
 * Mission split section (FR-113, Home's "Our Mission"). Per the client's
 * 2026-09-19 design review docx: background #001928, green #298B60
 * (`--color-dkgreen`) - both explicit client corrections, taking priority
 * over Figma's own values for this section (background #00405C, title/button
 * in the lighter `--color-green` #4DBA72). Eyebrow/body stay white, matching
 * both the docx and Figma.
 */
export function MissionSplitRenderer({ section }: SectionRendererProps) {
  const c = section.content as unknown as MissionSplitContent;

  if (LIGHT_VARIANT.has(section.key)) {
    const collage = DEFAULT_INVESTOR_BENEFITS_IMAGES[section.key];
    return (
      <section className="w-full bg-gradient-to-b from-[#f9f8f3] to-[#f5fdf9]">
        <div className="mx-auto flex w-full max-w-328 flex-col items-center gap-16 px-4 py-16 lg:flex-row lg:py-24">
          <div className="flex flex-1 flex-col gap-3">
            {c.eyebrow && <p className="text-p6 font-semibold text-teal uppercase">{c.eyebrow}</p>}
            <h2 className="text-h2 text-dkgreen">{c.title}</h2>
            {c.body?.map((paragraph, i) => (
              <p key={i} className={PUBLIC_BODY_CLASS}>
                {paragraph}
              </p>
            ))}
          </div>
          {collage && (
            <div className="flex flex-1 flex-col gap-8">
              <div className="flex gap-8">
                <img src={collage[0]} alt="" className="aspect-square w-1/2 rounded-2xl object-cover" />
                <img src={collage[1]} alt="" className="aspect-square w-1/2 rounded-2xl object-cover" />
              </div>
              <img src={collage[2]} alt="" className="aspect-[2/1] w-full rounded-2xl object-cover" />
            </div>
          )}
        </div>
      </section>
    );
  }

  // Falls back to the default image when the CMS has none set - see default-images.ts.
  const image = imageOrDefault(c.image, DEFAULT_MISSION_SPLIT_IMAGE[section.key]);

  return (
    <section className="bg-navy relative w-full overflow-hidden">
      <div className="mx-auto flex w-full max-w-328 flex-col items-start gap-10 px-4 py-16 lg:py-24">
        <div className="flex flex-col gap-6 lg:max-w-xl">
          {c.eyebrow && <p className="text-p6 font-semibold text-white uppercase">{c.eyebrow}</p>}
          <h2 className="text-h2 text-dkgreen">{c.title}</h2>
          {c.body?.map((paragraph, i) => (
            <p key={i} className="text-p4 text-white">
              {paragraph}
            </p>
          ))}
          {/* Extra top margin beyond the group's own gap - Figma gives the
            button noticeably more breathing room than the paragraph-to-
            paragraph spacing. */}
          {c.ctaLabel && c.ctaUrl && (
            <Button asChild variant="primary" className="mt-2 w-fit">
              <CtaLink to={c.ctaUrl}>{c.ctaLabel}</CtaLink>
            </Button>
          )}
        </div>
        {/* Mobile/tablet fallback - a normal contained image, in the flow. */}
        {image && (
          <img src={image} alt="" className="aspect-4/3 w-full rounded-2xl object-cover lg:hidden" />
        )}
      </div>
      {/* Desktop: full-bleed, spanning the section's entire top-to-bottom
        height and reaching its right edge - confirmed from Figma (728x849.8px
        image in an 852px-tall, 1728px-wide section) - not a contained rounded
        photo inside the padded row like every other split section uses. */}
      {image && (
        <img
          src={image}
          alt=""
          className="absolute inset-y-0 right-0 hidden h-full w-[42%] object-cover lg:block"
        />
      )}
    </section>
  );
}
